import {
	ICreateVideo,
	IEditVideo,
	IGetPaginatedVideos,
	IAssignVideos,
	ICreateVideoCategory,
	IUpdateVideoCategory,
	IGetPaginatedVideoCategories,
	ICreateVideoStudio,
	IUpdateVideoStudio,
	IGetPaginatedVideoStudios,
} from 'types';
import {
	serviceGetVideoInfo,
	serviceCreateVideo,
	serviceUpdateVideo,
	serviceGetPaginatedVideos,
	serviceDeleteVideo,
	serviceGetCustomersForAssignVideos,
	serviceGetRestaurantsByIDsCustomersForVideos,
	serviceGetVideosByIDsRestaurantsForVideos,
	serviceAssignVideos,
	serviceGetVideoCategoryInfo,
	serviceGetVideoCategoriesShortInfo,
	serviceCreateVideoCategory,
	serviceUpdateVideoCategory,
	serviceGetPaginatedVideoCategories,
	serviceDeleteVideoCategory,
	serviceGetVideoStudioInfo,
	serviceGetVideoStudiosShortInfo,
	serviceCreateVideoStudio,
	serviceUpdateVideoStudio,
	serviceGetPaginatedVideoStudios,
	serviceDeleteVideoStudio,
} from 'services/api/videos';
import { createAppErrorMessage } from 'utils/helpers';

const getVideoInfo = async (videoID: string) => {
	try {
		const response = await serviceGetVideoInfo(videoID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const createVideo = async (data: ICreateVideo) => {
	try {
		await serviceCreateVideo(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCreatingVideo');
		throw new Error(errorMessage);
	}
};

const editVideo = async (data: IEditVideo) => {
	try {
		await serviceUpdateVideo(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingVideo');
		throw new Error(errorMessage);
	}
};

const getPaginatedVideos = async (args: IGetPaginatedVideos) => {
	try {
		const response = await serviceGetPaginatedVideos(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const deleteVideo = async (videoID: string) => {
	try {
		await serviceDeleteVideo(videoID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorDeletingVideo');
		throw new Error(errorMessage);
	}
};

const getCustomersForAssignVideos = async () => {
	try {
		const response = await serviceGetCustomersForAssignVideos();
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getRestaurantsByIDsCustomersForVideos = async (customerIDs: string[]) => {
	try {
		const response = await serviceGetRestaurantsByIDsCustomersForVideos(customerIDs);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const getRestaurantsByIDsRestaurantsForVideos = async (restaurantIDs: string[]) => {
	try {
		const response = await serviceGetVideosByIDsRestaurantsForVideos(restaurantIDs);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const assignVideos = async (data: IAssignVideos[]) => {
	try {
		await serviceAssignVideos(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorAssignVideo');
		throw new Error(errorMessage);
	}
};

// VideoCategories
const getVideoCategoryInfo = async (categoryID: string) => {
	try {
		const response = await serviceGetVideoCategoryInfo(categoryID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getVideoCategoriesShortInfo = async () => {
	try {
		const response = await serviceGetVideoCategoriesShortInfo();
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const createVideoCategory = async (data: ICreateVideoCategory) => {
	try {
		await serviceCreateVideoCategory(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCreatingVideoCategory');
		throw new Error(errorMessage);
	}
};

const updateVideoCategory = async (data: IUpdateVideoCategory) => {
	try {
		await serviceUpdateVideoCategory(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingVideoCategory');
		throw new Error(errorMessage);
	}
};

const getPaginatedVideoCategories = async (args: IGetPaginatedVideoCategories) => {
	try {
		const response = await serviceGetPaginatedVideoCategories(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const deleteVideoCategory = async (categoryID: string) => {
	try {
		await serviceDeleteVideoCategory(categoryID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorDeletingVideoCategory');
		throw new Error(errorMessage);
	}
};
// VideoCategories

// VideoStudios
const getVideoStudioInfo = async (studioID: string) => {
	try {
		const response = await serviceGetVideoStudioInfo(studioID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getVideoStudiosShortInfo = async () => {
	try {
		const response = await serviceGetVideoStudiosShortInfo();
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const createVideoStudio = async (data: ICreateVideoStudio) => {
	try {
		await serviceCreateVideoStudio(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCreatingVideoStudio');
		throw new Error(errorMessage);
	}
};

const updateVideoStudio = async (data: IUpdateVideoStudio) => {
	try {
		await serviceUpdateVideoStudio(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingVideoStudio');
		throw new Error(errorMessage);
	}
};

const getPaginatedVideoStudios = async (args: IGetPaginatedVideoStudios) => {
	try {
		const response = await serviceGetPaginatedVideoStudios(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const deleteVideoStudio = async (studioID: string) => {
	try {
		await serviceDeleteVideoStudio(studioID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorDeletingVideoStudio');
		throw new Error(errorMessage);
	}
};
// VideoStudios

export {
	getVideoInfo,
	createVideo,
	editVideo,
	getPaginatedVideos,
	deleteVideo,
	getCustomersForAssignVideos,
	getRestaurantsByIDsCustomersForVideos,
	getRestaurantsByIDsRestaurantsForVideos,
	assignVideos,
	getVideoCategoryInfo,
	getVideoCategoriesShortInfo,
	createVideoCategory,
	updateVideoCategory,
	getPaginatedVideoCategories,
	deleteVideoCategory,
	getVideoStudioInfo,
	getVideoStudiosShortInfo,
	createVideoStudio,
	updateVideoStudio,
	getPaginatedVideoStudios,
	deleteVideoStudio,
};
